import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search, Loader2, ExternalLink, Download, FileSpreadsheet, FileText, FileJson, ChevronRight, BarChart3, Hash, Globe, Filter } from "lucide-react";
import { MultiSelectFilter, type MultiSelectOption } from "@/components/MultiSelectFilter";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { exportToExcel, exportToCSV, exportToJSON } from "@/lib/export-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { dataBrowserApi, serpApi } from "@/api";

interface SerpKeywordRow {
  request_id: string;
  target_keyword: string;
  target_url?: string;
  location_code: number;
  language_code: string;
  status: string;
  serp_results_count?: number;
  cost_dataforseo?: number;
  project_id?: string;
  project_name?: string;
  source?: 'internal' | 'external';
  created_at: string;
  completed_at?: string;
}

interface SerpResultRow {
  result_id: string;
  request_id: string;
  rank_absolute: number;
  rank_group?: number;
  domain?: string;
  title?: string;
  url: string;
  snippet?: string;
  item_type?: string;
  scraping_task_id?: string;
  target_keyword?: string;
  project_name?: string;
  scrape_status?: string;
  page_title?: string;
  word_count?: number;
  page_meta?: string;
  overall_score?: number;
  heading_score?: number;
  h1_count?: number;
  h2_count?: number;
  h3_count?: number;
  title_score?: number;
  description_score?: number;
}

// Format SERP result for enhanced export
function formatResultForExport(r: SerpResultRow, opts: { includeSeo: boolean }) {
  const base: Record<string, any> = {
    '排名': r.rank_absolute,
    '排名群組': r.rank_group || '—',
    '類型': r.item_type || 'organic',
    '關鍵字': r.target_keyword || '—',
    '網域': r.domain || '—',
    'URL': r.url,
    'SERP 標題': r.title || '—',
    '摘要': r.snippet || '—',
  };
  if (opts.includeSeo) {
    base['頁面標題'] = r.page_title || '—';
    base['Meta 描述'] = r.page_meta || '—';
    base['字數'] = r.word_count || 0;
    base['SEO 總分'] = r.overall_score || 0;
    base['標題分數'] = r.title_score || 0;
    base['描述分數'] = r.description_score || 0;
    base['標題結構分數'] = r.heading_score || 0;
    base['H1 數量'] = r.h1_count || 0;
    base['H2 數量'] = r.h2_count || 0;
    base['H3 數量'] = r.h3_count || 0;
    base['爬取狀態'] = r.scrape_status || '未爬取';
  }
  return base;
}

const SENTINEL_ALL = "__all__";

export default function SerpAnalysis() {
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState<Set<string>>(new Set());
  const [sourceFilter, setSourceFilter] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [rankFilter, setRankFilter] = useState(SENTINEL_ALL);
  const [includeSeo, setIncludeSeo] = useState(true);

  // Fetch all SERP keyword requests (filter client-side)
  const { data: keywords = [], isLoading: keywordsLoading } = useQuery({
    queryKey: ["serp-keywords-list"],
    queryFn: async () => {
      const r = await dataBrowserApi.serpKeywords({ limit: 500, status: "completed" });
      return (r.data || []) as SerpKeywordRow[];
    },
    refetchInterval: 60000,
  });

  // Derive project options from loaded keywords
  const projectOptions = useMemo<MultiSelectOption[]>(() => {
    const counts = new Map<string, { name: string; count: number }>();
    for (const kw of keywords) {
      if (kw.project_id && kw.project_name) {
        const entry = counts.get(kw.project_id);
        if (entry) entry.count++;
        else counts.set(kw.project_id, { name: kw.project_name, count: 1 });
      }
    }
    return Array.from(counts.entries()).map(([id, { name, count }]) => ({
      value: id, label: name, count,
    }));
  }, [keywords]);

  // Derive source options from loaded keywords
  const sourceOptions = useMemo<MultiSelectOption[]>(() => {
    const counts = new Map<string, number>();
    for (const kw of keywords) {
      const src = kw.source || 'internal';
      counts.set(src, (counts.get(src) || 0) + 1);
    }
    const labelMap: Record<string, string> = { internal: '內部 (App04)', external: '外部 (API)' };
    return Array.from(counts.entries()).map(([src, count]) => ({
      value: src, label: labelMap[src] || src, count,
    }));
  }, [keywords]);

  // Fetch SERP results for selected keyword
  const { data: serpResults = [], isLoading: resultsLoading } = useQuery({
    queryKey: ["serp-results-by-request", selectedRequestId],
    queryFn: async () => {
      if (!selectedRequestId) return [];
      const r = await dataBrowserApi.serpResults({ request_id: selectedRequestId, limit: 100 });
      return (r.data || []) as SerpResultRow[];
    },
    enabled: !!selectedRequestId,
  });

  // Filter keywords by project, source, and search
  const filteredKeywords = useMemo(() => {
    return keywords.filter(k => {
      if (projectFilter.size > 0 && !projectFilter.has(k.project_id || '')) return false;
      if (sourceFilter.size > 0 && !sourceFilter.has(k.source || 'internal')) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!k.target_keyword.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [keywords, projectFilter, sourceFilter, searchQuery]);

  // Selected keyword info
  const selectedKeyword = keywords.find(k => k.request_id === selectedRequestId);

  // Filter results by rank
  const displayResults = useMemo(() => {
    if (rankFilter === SENTINEL_ALL) return serpResults;
    const n = parseInt(rankFilter, 10);
    return serpResults.filter(r => r.rank_absolute <= n);
  }, [serpResults, rankFilter]);

  // Stats
  const stats = serpResults.length > 0 ? {
    total: serpResults.length,
    top3: serpResults.filter(r => r.rank_absolute <= 3).length,
    top10: serpResults.filter(r => r.rank_absolute <= 10).length,
    top20: serpResults.filter(r => r.rank_absolute <= 20).length,
    scraped: serpResults.filter(r => r.scrape_status === "completed").length,
    avgScore: Math.round(serpResults.reduce((s, r) => s + (r.overall_score || 0), 0) / serpResults.filter(r => r.overall_score).length || 0),
  } : null;

  // Export handler
  const handleExport = (format: 'excel' | 'csv' | 'json') => {
    if (displayResults.length === 0) {
      toast.warning("沒有資料可以匯出");
      return;
    }
    const formatted = displayResults.map(r => formatResultForExport(r, { includeSeo }));
    const kw = selectedKeyword?.target_keyword || 'serp';
    const filename = `serp_${kw}_${new Date().toISOString().split('T')[0]}`;

    try {
      switch (format) {
        case 'excel':
          exportToExcel(formatted, { filename: `${filename}.xlsx`, sheetName: 'SERP 排名' });
          break;
        case 'csv':
          exportToCSV(formatted, { filename: `${filename}.csv` });
          break;
        case 'json':
          exportToJSON(displayResults, { filename: `${filename}.json` });
          break;
      }
      toast.success(`${format.toUpperCase()} 匯出成功`);
    } catch (error: any) {
      toast.error(`匯出失敗: ${error.message}`);
    }
  };

  // Export entire project (server-side, includes MD/HTML)
  const handleProjectExport = async (format: 'excel' | 'json') => {
    const pid = projectFilter.size === 1 ? [...projectFilter][0] : undefined;
    if (!pid) {
      toast.warning("請選擇單一專案再使用專案匯出");
      return;
    }
    try {
      await serpApi.exportProject(pid, {
        keyword: selectedKeyword?.target_keyword,
        format,
        include_markdown: true,
        include_html: false,
        include_seo: true,
      });
      toast.success(`專案匯出成功（含 MD + SEO + H 標籤）`);
    } catch (error: any) {
      toast.error(`匯出失敗: ${error.message}`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">SERP 分析</h1>
        <p className="text-muted-foreground">搜尋引擎排名分析與關鍵字研究</p>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <MultiSelectFilter
                label="全部專案"
                options={projectOptions}
                selected={projectFilter}
                onChange={setProjectFilter}
              />
              <MultiSelectFilter
                label="全部來源"
                options={sourceOptions}
                selected={sourceFilter}
                onChange={setSourceFilter}
              />
            </div>

            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜尋關鍵字..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={rankFilter} onValueChange={setRankFilter}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SENTINEL_ALL}>全部排名</SelectItem>
                <SelectItem value="3">Top 3</SelectItem>
                <SelectItem value="10">Top 10</SelectItem>
                <SelectItem value="20">Top 20</SelectItem>
                <SelectItem value="50">Top 50</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-auto">
              <div className="flex items-center gap-1.5">
                <Switch id="include-seo" checked={includeSeo} onCheckedChange={setIncludeSeo} />
                <Label htmlFor="include-seo" className="text-xs">含 SEO/H 標籤</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main content: master-detail layout */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left: Keyword list */}
        <div className="col-span-12 lg:col-span-4 xl:col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">關鍵字 ({filteredKeywords.length})</CardTitle>
                {keywordsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-320px)] min-h-[400px]">
                {filteredKeywords.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8 text-sm">
                    {searchQuery ? "無符合條件的關鍵字" : "尚無關鍵字記錄"}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredKeywords.map(kw => (
                      <button
                        key={kw.request_id}
                        onClick={() => setSelectedRequestId(kw.request_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-center gap-2 ${
                          selectedRequestId === kw.request_id ? "bg-primary/10 border-l-2 border-primary" : ""
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{kw.target_keyword}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {kw.serp_results_count || 0} 筆
                            </span>
                            {kw.source === 'external' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-400 text-orange-500">
                                API
                              </Badge>
                            )}
                            {kw.project_name && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                {kw.project_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right: Results */}
        <div className="col-span-12 lg:col-span-8 xl:col-span-9 space-y-4">
          {selectedRequestId && selectedKeyword ? (
            <>
              {/* Stats row */}
              {stats && (
                <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                  <Card>
                    <CardContent className="pt-4 pb-3 px-4">
                      <p className="text-xs text-muted-foreground">總結果</p>
                      <p className="text-xl font-bold">{stats.total}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3 px-4">
                      <p className="text-xs text-muted-foreground">Top 3</p>
                      <p className="text-xl font-bold text-green-600">{stats.top3}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3 px-4">
                      <p className="text-xs text-muted-foreground">Top 10</p>
                      <p className="text-xl font-bold text-blue-600">{stats.top10}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3 px-4">
                      <p className="text-xs text-muted-foreground">Top 20</p>
                      <p className="text-xl font-bold text-yellow-600">{stats.top20}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3 px-4">
                      <p className="text-xs text-muted-foreground">已爬取</p>
                      <p className="text-xl font-bold">{stats.scraped}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 pb-3 px-4">
                      <p className="text-xs text-muted-foreground">平均 SEO 分</p>
                      <p className="text-xl font-bold">{stats.avgScore || '—'}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Results table */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        「{selectedKeyword.target_keyword}」排名結果
                      </CardTitle>
                      <CardDescription className="mt-1">
                        顯示 {displayResults.length} / {serpResults.length} 筆
                        {resultsLoading && <span className="ml-2 text-xs">(載入中...)</span>}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Client-side export */}
                      {displayResults.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Download className="h-4 w-4 mr-1.5" />
                              匯出
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleExport('excel')}>
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              Excel（排名 + {includeSeo ? 'SEO/H標籤' : '基本'}）
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleExport('csv')}>
                              <FileText className="h-4 w-4 mr-2" />
                              CSV（排名 + {includeSeo ? 'SEO/H標籤' : '基本'}）
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleExport('json')}>
                              <FileJson className="h-4 w-4 mr-2" />
                              JSON（完整原始資料）
                            </DropdownMenuItem>
                            {projectFilter.size === 1 && (
                              <>
                                <DropdownMenuItem onClick={() => handleProjectExport('excel')}>
                                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                                  專案匯出 Excel（含 MD + H 標籤）
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleProjectExport('json')}>
                                  <FileJson className="h-4 w-4 mr-2" />
                                  專案匯出 JSON（含 MD + H 標籤）
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {resultsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : displayResults.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <p>尚無排名結果</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-14 text-center">#</TableHead>
                            <TableHead>類型</TableHead>
                            <TableHead>網域</TableHead>
                            <TableHead className="min-w-[200px]">標題</TableHead>
                            <TableHead>URL</TableHead>
                            {includeSeo && (
                              <>
                                <TableHead className="text-center w-16">
                                  <span title="SEO 總分">SEO</span>
                                </TableHead>
                                <TableHead className="text-center w-14">
                                  <span title="字數">字數</span>
                                </TableHead>
                                <TableHead className="text-center w-20">
                                  <span title="H1 / H2 / H3 數量">H 標籤</span>
                                </TableHead>
                              </>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {displayResults.map(result => (
                            <TableRow key={result.result_id}>
                              <TableCell className="text-center">
                                <span className={`font-bold ${
                                  result.rank_absolute <= 3 ? 'text-green-600' :
                                  result.rank_absolute <= 10 ? 'text-blue-600' :
                                  result.rank_absolute <= 20 ? 'text-yellow-600' :
                                  'text-muted-foreground'
                                }`}>
                                  {result.rank_absolute}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-[10px]">
                                  {result.item_type || "organic"}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-medium text-sm">
                                <div className="flex items-center gap-1">
                                  <Globe className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate max-w-[140px]">{result.domain}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <p className="text-sm line-clamp-2" title={result.title}>
                                  {result.title || '—'}
                                </p>
                                {result.snippet && (
                                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5" title={result.snippet}>
                                    {result.snippet}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell>
                                <a
                                  href={result.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline flex items-center gap-1 text-xs font-mono"
                                  title={result.url}
                                >
                                  <span className="truncate max-w-[180px]">{result.url}</span>
                                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                </a>
                              </TableCell>
                              {includeSeo && (
                                <>
                                  <TableCell className="text-center">
                                    {result.overall_score ? (
                                      <Badge variant={result.overall_score >= 70 ? "default" : result.overall_score >= 40 ? "secondary" : "destructive"} className="text-[10px]">
                                        {result.overall_score}
                                      </Badge>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center text-sm">
                                    {result.word_count || '—'}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {(result.h1_count !== undefined && result.h1_count !== null) ? (
                                      <div className="flex items-center justify-center gap-0.5 text-xs" title={`H1: ${result.h1_count}, H2: ${result.h2_count}, H3: ${result.h3_count}`}>
                                        <Hash className="h-3 w-3 text-muted-foreground" />
                                        <span>{result.h1_count}/{result.h2_count}/{result.h3_count}</span>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-16">
                <div className="text-center text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p className="text-lg mb-1">選擇關鍵字查看排名結果</p>
                  <p className="text-sm">從左側列表點選關鍵字，即可查看 SERP 排名、SEO 分數與 H 標籤分析</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
